import React from 'react';
import { TextField, MenuItem, IconButton, Typography, Box, Select } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormLabelStyled } from '../../../common/Dialog/DialogGlynt';
import AddNewButton from './AddNewButton';
import { TemplateFilter, FilterCondition } from '../../../../types/packager-types';
import { AvailableColumn } from '../../../hooks/packager/useGetAvailableColumns';
import { getFilterConditionsForField } from '../templateEditorConstants';

interface TemplateFiltersListProps {
  filters: TemplateFilter[];
  fields: AvailableColumn[];
  onAdd: () => void;
  onUpdate: (index: number, updates: Partial<TemplateFilter>) => void;
  onUpdateValue: (
    index: number,
    value: string | string[] | [string, string],
    subIndex?: number
  ) => void;
  onRemove: (index: number) => void;
}

const inputSx = {
  fontSize: 13,
  height: 32,
  minHeight: 32,
  backgroundColor: 'background.paper',
};

const FilterValueInput: React.FC<{
  filter: TemplateFilter;
  index: number;
  onUpdateValue: TemplateFiltersListProps['onUpdateValue'];
}> = ({ filter, index, onUpdateValue }) => {
  if (filter.condition === FilterCondition.BETWEEN) {
    const value = Array.isArray(filter.value) ? filter.value : ['', ''];
    return (
      <>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Min"
          value={value[0] || ''}
          onChange={(e) => onUpdateValue(index, e.target.value, 0)}
          sx={{ width: 80 }}
          InputProps={{ sx: inputSx }}
        />
        <TextField
          size="small"
          variant="outlined"
          placeholder="Max"
          value={value[1] || ''}
          onChange={(e) => onUpdateValue(index, e.target.value, 1)}
          sx={{ width: 80 }}
          InputProps={{ sx: inputSx }}
        />
      </>
    );
  }

  if (filter.condition === FilterCondition.IS_ONE_OF) {
    const value = Array.isArray(filter.value) ? filter.value.join(', ') : '';
    return (
      <TextField
        size="small"
        variant="outlined"
        placeholder="Comma-separated values"
        value={value}
        onChange={(e) =>
          onUpdateValue(
            index,
            e.target.value
              .split(',')
              .map((v) => v.trim())
              .filter(Boolean)
          )
        }
        sx={{ flexGrow: 0.85, minWidth: 120, maxWidth: '85%' }}
        InputProps={{ sx: inputSx }}
      />
    );
  }

  return (
    <TextField
      size="small"
      variant="outlined"
      placeholder="Value"
      value={filter.value as string}
      onChange={(e) => onUpdateValue(index, e.target.value)}
      sx={{ flexGrow: 1, minWidth: 120 }}
      InputProps={{ sx: inputSx }}
    />
  );
};

const TemplateFiltersList: React.FC<TemplateFiltersListProps> = ({
  filters,
  fields,
  onAdd,
  onUpdate,
  onUpdateValue,
  onRemove,
}) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <FormLabelStyled sx={{ mb: 0 }}>FILTERS</FormLabelStyled>
        <AddNewButton onClick={onAdd} />
      </Box>
      <Box
        sx={{
          mb: 2,
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'grey.50',
        }}
      >
        {filters.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
            <Typography variant="body2">No filters - all rows included</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', px: 1 }}>
              Include rows where ALL conditions match
            </Typography>
            {filters.map((filter, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                }}
              >
                <Select
                  size="small"
                  variant="outlined"
                  value={filter.field}
                  onChange={(e) => onUpdate(index, { field: e.target.value })}
                  sx={{
                    width: 240,
                    minWidth: 240,
                    flexShrink: 0,
                    fontSize: 13,
                    height: 32,
                    minHeight: 32,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {fields.map((f) => (
                    <MenuItem key={f.name} value={f.name}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  size="small"
                  variant="outlined"
                  value={filter.condition}
                  onChange={(e) => onUpdate(index, { condition: e.target.value as FilterCondition })}
                  sx={{
                    width: 120,
                    fontSize: 13,
                    height: 32,
                    minHeight: 32,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {getFilterConditionsForField(filter.field, fields).map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
                <FilterValueInput filter={filter} index={index} onUpdateValue={onUpdateValue} />
                <IconButton size="small" onClick={() => onRemove(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TemplateFiltersList;
